import { z } from 'zod'
import { isDatabaseVersionSupported } from '@/config/database-versions'
import {
  OBJECT_STORAGE_NAME_MAX_LENGTH,
  OBJECT_STORAGE_NAME_MIN_LENGTH,
} from '@/routes/databases/create-database/form/sections/external-backups/constants'
import type { ClusterProvider, DatabaseType } from '@/graphql.types'

const invalidDBNames: {
  [key in DatabaseType]?: string[]
} = {
  mysql: ['mysql', 'information_schema', 'performance_schema', 'sys'],
  postgresql: [
    'postgres',
    'template0',
    'template1',
    'information_schema',
    'performance_schema',
    'sys',
  ],
}

export const schema = z
  .object({
    idCompany: z.string(),
    idProject: z.string().optional(),
    idCluster: z.string(),
    displayName: z.string().min(1, {
      message: 'Name is required',
    }),
    data: z.object({
      dbName: z
        .string()
        .min(2, { message: 'Name is required' })
        .max(100, { message: 'Name must be less than 100 characters' })
        .trim()
        .regex(/^[a-zA-Z0-9_\-+]+$/, {
          message: 'Name contains invalid characters',
        }),
      dbUser: z
        .string()
        .min(2, { message: 'User is required' })
        .max(100, { message: 'User must be less than 100 characters' })
        .trim()
        .regex(/^[a-zA-Z0-9_\-+]+$/, {
          message: 'User contains invalid characters',
        })
        .refine((val) => val.toLowerCase() !== 'root', {
          message: 'User cannot be root',
        }),
      dbPassword: z
        .string()
        .trim()
        .min(4, 'Password min length is 4 characters')
        .max(100, 'Password max length is 100 characters')
        .regex(/^[a-zA-Z0-9_?<>#@\-=+]+$/, {
          message: 'Password contains invalid characters',
        }),
    }),
    extensions: z
      .object({
        enableVector: z.boolean().default(false).nullish(),
        enablePostgis: z.boolean().default(false).nullish(),
      })
      .optional(),
    type: z.enum(['valkey', 'redis', 'mysql', 'postgresql', 'mariadb']),
    version: z.string().min(1, {
      message: 'Version is required',
    }),
    idResourceType: z.string(),
    externalBackup: z.object({
      isEnabled: z.boolean(),
      storageMode: z.enum(['existing', 'new']),
      idObjectStorage: z.string(),
      objectStorageDisplayName: z.string(),
    }),
  })
  .refine(
    ({ type, data }) =>
      !invalidDBNames[type]?.includes(data.dbName.toLowerCase()),
    { message: 'Database name is not allowed', path: ['data', 'dbName'] }
  )
  .refine(
    ({ type, extensions, version }) => {
      if (
        type === 'postgresql' &&
        extensions?.enablePostgis &&
        parseFloat(version) < 13
      ) {
        return false
      }
      return true
    },
    {
      message: 'PostGIS extension requires PostgreSQL version 13 or higher',
      path: ['extensions', 'enablePostgis'],
    }
  )
  .refine(
    ({ type, extensions, version }) => {
      if (
        type === 'postgresql' &&
        extensions?.enableVector &&
        parseFloat(version) < 11
      ) {
        return false
      }
      return true
    },
    {
      message: 'Vector extension requires PostgreSQL version 11 or higher',
      path: ['extensions', 'enableVector'],
    }
  )
  .refine(
    ({ externalBackup }) =>
      !externalBackup.isEnabled ||
      externalBackup.storageMode !== 'existing' ||
      externalBackup.idObjectStorage.length > 0,
    {
      message: 'Select an object storage',
      path: ['externalBackup', 'idObjectStorage'],
    }
  )
  .refine(
    ({ externalBackup }) => {
      if (!externalBackup.isEnabled || externalBackup.storageMode !== 'new') {
        return true
      }
      const length = externalBackup.objectStorageDisplayName.trim().length
      return (
        length >= OBJECT_STORAGE_NAME_MIN_LENGTH &&
        length <= OBJECT_STORAGE_NAME_MAX_LENGTH
      )
    },
    {
      message: `Name must be between ${OBJECT_STORAGE_NAME_MIN_LENGTH} and ${OBJECT_STORAGE_NAME_MAX_LENGTH} characters`,
      path: ['externalBackup', 'objectStorageDisplayName'],
    }
  )

export const withSupportedVersionSchema = (
  providerByIdCluster: Record<string, ClusterProvider>
) =>
  schema.refine(
    ({ type, version, idCluster }) =>
      isDatabaseVersionSupported({
        type,
        version,
        provider: providerByIdCluster[idCluster],
      }),
    {
      message: 'This version is not available in the selected location',
      path: ['version'],
    }
  )

export type CreateDatabaseFormValues = z.infer<typeof schema>
